/**
 * Bulk-create — pure pricing / quote-derivation helpers.
 *
 * No UI. Shared by the review table (row summaries), the totals bar, and the
 * order-create request builder.
 */
import type { PricingOption } from "@/api.types.v2";
import type { BulkPickupConfig, ParsedOrder, RowPickup } from "./types";

/** Synthetic stable key for a quote option: (carrier, carrierService). */
export const makeQuoteId = (option: PricingOption): string =>
  `${option.carrier.id}__${option.carrierService.code}`;

/**
 * Default quote selection. Pricing-redesign: pick the fastest option by
 * estimated days (legacy preferred a service tier that no longer exists).
 */
export const selectDefaultQuote = (
  options: PricingOption[]
): PricingOption | undefined =>
  options
    .slice()
    .sort(
      (a, b) =>
        (a.carrierService.estimatedDays ?? 999) -
        (b.carrierService.estimatedDays ?? 999)
    )[0];

/** A slot belongs to one quoted service; another tier's price is not its price. */
export const pickupTierForSlot = (option: PricingOption, slotId: string) => {
  const matches = option.pickupOptions.filter((tier) =>
    tier.timeSlots.some((slot) => slot.id === slotId)
  );
  return matches.length === 1 ? matches[0] : undefined;
};

/**
 * Tax-inclusive per-row charge from the tier that owns the selected slot.
 */
export const orderChargeCents = (order: ParsedOrder): number => {
  const opt = order.selectedQuote;
  if (!opt) return 0;
  const labelCharge = opt.labelPriceCents + (opt.taxCents ?? 0);
  if (!order.pickup || opt.pickupOptions.length === 0) return labelCharge;
  const tier = pickupTierForSlot(opt, order.pickup.slotId);
  return tier
    ? labelCharge + tier.pickupPriceCents + (tier.taxCents ?? 0)
    : labelCharge;
};

/** Sum of charges across rows that are selected + quoted (ready or completed). */
export const totalChargeCents = (orders: ParsedOrder[]): number =>
  orders
    .filter(
      (o) =>
        (o.status === "ready" || o.status === "completed") &&
        o.selectedQuote &&
        o.selected
    )
    .reduce((sum, o) => sum + orderChargeCents(o), 0);

/** Format CAD cents for a given locale. */
export const formatCurrency = (cents: number, locale?: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);

/** Render a pickup choice as e.g. "Wed May 7 · Morning". */
export const formatPickupLabel = (
  pickup: NonNullable<RowPickup>,
  config: BulkPickupConfig | null
): string => {
  const slot = config?.timeSlots.find((s) => s.id === pickup.slotId);
  const dateLabel = pickup.date
    ? new Date(pickup.date).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "—";
  if (!slot) return dateLabel;
  return `${dateLabel} · ${slot.name}`;
};
